import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { IndianRupee, Lock, Trash2, Loader2, Eye, EyeOff, Activity, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

function CalibrationCard() {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [cal, setCal] = useState(null);
  const [isRunning, setIsRunning] = useState(false);

  const loadCal = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/calibration`);
      setCal(await res.json());
    } catch { /* ignore */ }
  };
  useEffect(() => { loadCal(); }, []);

  const runCalibration = async () => {
    setIsRunning(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/calibration/run`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Recalibrated from ${data.sample_size} submissions`);
        setCal({ calibrated: true, ...data });
      } else {
        toast.error(data.detail || 'Calibration failed');
      }
    } catch { toast.error('Calibration failed'); }
    setIsRunning(false);
  };

  const resetCalibration = async () => {
    try {
      await fetch(`${API_URL}/api/admin/calibration/reset`, { method: 'POST' });
      toast.success('Reverted to default weights');
      loadCal();
    } catch { toast.error('Reset failed'); }
  };

  const skewed = (cal?.item_analysis || []).filter((q) => q.skewed);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Algorithm Calibration
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-500 mb-4">
          Recomputes archetype scoring weights from real quiz data so results get sharper as more people take the quiz. Also flags questions where answers are heavily one-sided.
        </p>
        {cal?.calibrated ? (
          <p className="text-xs text-gray-500 mb-3" data-testid="calibration-status">
            Last calibrated {cal.calibrated_at ? new Date(cal.calibrated_at).toLocaleString() : '—'} from {cal.sample_size} submissions.
          </p>
        ) : (
          <p className="text-xs text-gray-500 mb-3" data-testid="calibration-status">Using default weights — never calibrated.</p>
        )}
        <div className="flex gap-3 mb-4">
          <Button onClick={runCalibration} disabled={isRunning} style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }} className="text-white hover:opacity-90" data-testid="run-calibration-btn">
            {isRunning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Activity className="h-4 w-4 mr-2" />}
            Run Calibration
          </Button>
          {cal?.calibrated && (
            <Button variant="outline" onClick={resetCalibration} data-testid="reset-calibration-btn">
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
          )}
        </div>
        {cal?.weights && (
          <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-xs text-gray-500 mb-4" data-testid="calibration-weights">
            {Object.entries(cal.weights).map(([a, w]) => (
              <div key={a} className="flex justify-between"><span>{a}</span><span className="font-mono">{Number(w).toFixed(2)}</span></div>
            ))}
          </div>
        )}
        {skewed.length > 0 && (
          <div data-testid="skewed-questions">
            <p className="text-xs font-semibold text-amber-500 mb-1">⚠ {skewed.length} question{skewed.length > 1 ? 's' : ''} with heavily one-sided answers (consider rewording):</p>
            <ul className="text-xs text-gray-500 space-y-0.5">
              {skewed.map((q) => (
                <li key={q.question}>{q.question}: {q.leftTrait} vs {q.rightTrait} — {q.left_pct}% choose left</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function AdminSettingsTab({
  reportPriceInr, setReportPriceInr, isSavingPrice, handleSavePrice,
  basePriceInr, setBasePriceInr, isSavingBasePrice, handleSaveBasePrice,
  currentPassword, setCurrentPassword, newPassword, setNewPassword,
  confirmPassword, setConfirmPassword, isChangingPassword,
  showCurrentPw, setShowCurrentPw, showNewPw, setShowNewPw,
  handleChangePassword, loadSubmissions, loadReports, loadAnalytics,
}) {
  const handleClearData = async () => {
    if (!window.confirm('Are you sure? This will permanently delete ALL user data.')) return;
    if (!window.confirm('This action cannot be undone. Proceed?')) return;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/admin/clear-test-data`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success(`Cleared: ${data.cleared.submissions} submissions, ${data.cleared.reports} reports`);
        loadSubmissions(); loadReports(); loadAnalytics();
      } else { toast.error('Failed to clear data'); }
    } catch { toast.error('Failed to clear data'); }
  };

  return (
    <div className="max-w-lg space-y-6">
      {/* Report Pricing */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <IndianRupee className="h-5 w-5" />
            Report Pricing
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSavePrice} className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-sm font-medium">Premium Report Price (INR)</Label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">₹</span>
                  <Input
                    type="number"
                    value={reportPriceInr}
                    onChange={(e) => setReportPriceInr(e.target.value)}
                    className="pl-7"
                    min="0"
                    step="1"
                    placeholder="2499"
                    required
                    data-testid="report-price-input"
                  />
                </div>
                <Button type="submit" disabled={isSavingPrice} style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }} className="text-white hover:opacity-90 px-6" data-testid="save-price-btn">
                  {isSavingPrice ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">Set to 0 to make reports free.</p>
            </div>
          </form>

          {/* Marketing / Anchor Price — shown as strike-through on Results page */}
          <form onSubmit={handleSaveBasePrice} className="space-y-4 mt-6 pt-6 border-t border-gray-700">
            <div>
              <Label className="mb-1.5 block text-sm font-medium">Marketing Anchor Price (INR) <span className="text-gray-500 font-normal">— strike-through</span></Label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">₹</span>
                  <Input
                    type="number"
                    value={basePriceInr}
                    onChange={(e) => setBasePriceInr(e.target.value)}
                    className="pl-7"
                    min="0"
                    step="1"
                    placeholder="e.g. 2499"
                    data-testid="report-base-price-input"
                  />
                </div>
                <Button type="submit" disabled={isSavingBasePrice} style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }} className="text-white hover:opacity-90 px-6" data-testid="save-base-price-btn">
                  {isSavingBasePrice ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">Higher marketing price used for strike-through above the discount. Leave empty to hide.</p>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Algorithm Calibration */}
      <CalibrationCard />

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Lock className="h-5 w-5" />Change Password</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-sm font-medium">Current Password</Label>
              <div className="relative">
                <Input type={showCurrentPw ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password" required data-testid="current-password-input" />
                <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block text-sm font-medium">New Password</Label>
              <div className="relative">
                <Input type={showNewPw ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 4 characters" required data-testid="new-password-input" />
                <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block text-sm font-medium">Confirm New Password</Label>
              <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" required data-testid="confirm-password-input" />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>
            <Button type="submit" disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword} style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }} className="w-full text-white hover:opacity-90" data-testid="change-password-btn">
              {isChangingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Clear All Data */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600"><Trash2 className="h-5 w-5" />Clear All User Data</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">Permanently delete all submissions, reports, payment orders, shared results, and OTPs. Coupons, admin password, and report price are preserved.</p>
          <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="clear-test-data-btn" onClick={handleClearData}>
            <Trash2 className="h-4 w-4 mr-2" />
            Clear All User Data
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
